import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useSWR from "swr";
import urls from "../../api/urls"
import {api, HydraData} from "../../api/apiProvider";
import {Button, Card, Checkbox, Col, Flex, Form, message, Row, Space, Upload} from "antd";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";
import {useParams} from "react-router-dom";
import Title from "antd/es/typography/Title";
import Stages from "../../components/brief/Stages";
import type {GetProp, UploadFile, UploadProps} from 'antd';
import IconXls from "../../components/ui/icons/IconXls";
import IconDelete from "../../components/ui/icons/IconDelete";
import IconUploadFile from "../../components/ui/icons/IconUploadFile";
import {BriefTask, MediaObject} from "../../api/types";
import Roles from "../../roles/Roles";
import getBriefParamsString from "../../api/params";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

interface TaskData {
  file: string,
  isClientConfirmed: boolean,
  isEconometristAccepted: boolean,
  id?: number,
  contentUrl?: string,
  originalName?: string,
}

const uploadFile = async (formData: any) => {
  return await api.postFile(`${urls.mediaObjects}`, {arg: formData})
};

const deleteFile = async (fileId: number) => {
  return await api.delete(`${urls.mediaObjects}/${fileId}`)
};

const addFileToBriefTask = async (taskId: number, fileIri: string) => {
  return await api.patch(`${urls.briefTasks}/${taskId}`, {
    arg: {
      file: fileIri
    }
  })
};

const addBriefTaskData = async (data: any) => {
  return await api.post(`${urls.briefTasksData}`, {arg: data})
};

const updateBriefTaskData = async (taskDataId: number, data: any) => {
  return await api.patch(`${urls.briefTasksData}/${taskDataId}`, {arg: data})
};

const deleteBriefTaskData = async (taskDataId: number) => {
  return await api.delete(`${urls.briefTasksData}/${taskDataId}`)
};

export default function Dataset() {
  const {t} = useTranslation();
  const routeParams = useParams();
  const briefId = Number(routeParams.id) || 0;

  const [datasetTemplateForm] = Form.useForm();
  const [datasetForm] = Form.useForm();
  const [datasetTemplateFileList, setDatasetTemplateFileList] = useState<UploadFile[]>([]);
  const [datasetFileList, setDatasetFileList] = useState<UploadFile[]>([]);
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);
  const [isDatasetTemplateLoading, setIsDatasetTemplateLoading] = useState(false);
  const [isDatasetLoading, setIsDatasetLoading] = useState(false);

  const {
    data: brief,
  } = useSWR<HydraData>(briefId > 0 && briefId !== 0 ? `${urls.briefs}/${briefId}?${getBriefParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  });

  const getBriefCommonTaskId = () => {
    if (!brief || !brief.data || !brief.data.tasks || brief.data.tasks.length === 0) {
      return null;
    }

    const commonTask = brief.data.tasks.find((task: BriefTask) => {
      return task.taskType.code === 'common'
    });

    if (commonTask && commonTask.id) {
      return commonTask.id
    }

    return null
  };

  const {
    data: briefTask,
    mutate: mutateBriefTask,
  } = useSWR<HydraData>(getBriefCommonTaskId()
    ? `${urls.briefTasks}/${getBriefCommonTaskId()}` : null, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: briefTaskFile,
  } = useSWR<HydraData>((briefTask && briefTask.data && briefTask.data.file) ? `${urls.mediaObjects}/${briefTask.data.file.split('/').pop()}` : null, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: briefTaskData,
    mutate: mutateBriefTaskData,
  } = useSWR<HydraData>((briefTask && briefTask.data) ? `${urls.briefTasksData}?briefTask.id=${briefTask.data.id}` : null, api.get, {
    revalidateOnFocus: false
  });

  const getBriefTaskDataUrl = (): string | null => {
    if (!briefTaskData || !briefTaskData.data || briefTaskData.data.length === 0) {
      return null
    }

    const filesIds = briefTaskData.data.filter((taskData: TaskData) => taskData.file !== null).map((taskData: TaskData) => taskData.file.split('/').pop());
    return `${urls.mediaObjects}?${filesIds.map((fileId: string) => `id[]=${fileId}`).join('&')}`
  };

  const {
    data: briefTaskDataFiles,
  } = useSWR<HydraData>(getBriefTaskDataUrl(), api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (briefTaskData && briefTaskData.data && briefTaskDataFiles && briefTaskDataFiles.data) {
      const tasks: TaskData[] = [];

      for (let taskData of briefTaskData.data) {
        const file = briefTaskDataFiles.data.find((file: MediaObject) => file['@id'] === taskData.file);

        tasks.push({
          isClientConfirmed: taskData.isClientConfirmed,
          isEconometristAccepted: taskData.isEconometristAccepted,
          file: taskData.file,
          id: taskData.id,
          contentUrl: file ? file.contentUrl : '',
          originalName: file ? file.originalName : '',
        })
      }

      datasetForm.setFieldValue('tasks', tasks)
    } else {
      datasetForm.setFieldValue('tasks', [])
    }
  }, [briefTaskData, briefTaskDataFiles]);

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const uploadDatasetTemplateProps: UploadProps = {
    onRemove: (file: UploadFile) => {
      const index = datasetTemplateFileList.indexOf(file);
      const newFileList = datasetTemplateFileList.slice();
      newFileList.splice(index, 1);
      setDatasetTemplateFileList(newFileList);
      setIsValuesChanged(true)
    },
    beforeUpload: (file) => {
      setDatasetTemplateFileList([...datasetTemplateFileList, file]);
      setIsValuesChanged(true);
      return false
    },
  };

  const uploadDatasetProps: UploadProps = {
    onRemove: (file: UploadFile) => {
      const index = datasetFileList.indexOf(file);
      const newFileList = datasetFileList.slice();
      newFileList.splice(index, 1);
      setDatasetFileList(newFileList);
      setIsValuesChanged(true)
    },
    beforeUpload: (file) => {
      setDatasetFileList([...datasetFileList, file]);
      setIsValuesChanged(true);
      return false
    },
  };

  const onDatasetTemplateSave = (values: any) => {
    if (!Roles.hasAccess('canSetTemplateInDataset')) {
      return
    }

    const formData = new FormData();

    if (!datasetTemplateFileList.length) {
      return
    }

    setIsDatasetTemplateLoading(true);

    datasetTemplateFileList.forEach((file) => {
      formData.append('file', file as FileType);
    });

    uploadFile(formData).then(response => {
      if (response && response['@id'] && brief && brief.data) {
        addFileToBriefTask(getBriefCommonTaskId(), response['@id'])
          .then(response => {
            message.success(t('common.saved'));
            setDatasetTemplateFileList([]);
            datasetTemplateForm.setFieldValue('file', null);
            mutateBriefTask();
            setIsValuesChanged(false)
          })
      }
    }).catch((response) => {
      message.error(response['hydra:description']);
      setIsDatasetTemplateLoading(false)
    }).finally(() => {
      setIsDatasetTemplateLoading(false)
    })
  };

  const allTasksDataAccepted = () => {
    if (briefTaskData && briefTaskData.data && briefTaskData.data.length > 0) {
      return briefTaskData.data.every((taskData: TaskData) => taskData.isClientConfirmed)
    }

    return false
  };

  const onDatasetSave = (values: any) => {
    const formData = new FormData();

    if (datasetFileList.length > 0) {
      setIsDatasetLoading(true);
      datasetFileList.forEach((file) => {
        formData.append('file', file as FileType);
      });

      uploadFile(formData).then(response => {
        if (response && response['@id'] && briefTask && briefTask.data && briefTask.data['@id']) {
          addBriefTaskData({
            briefTask: briefTask.data['@id'],
            file: response['@id'],
            isClientConfirmed: false,
            isEconometristAccepted: false,
          }).then(response => {
            message.success(t('common.saved'));
            setDatasetFileList([]);
            datasetForm.setFieldValue('file', null);
            mutateBriefTaskData();
            setIsValuesChanged(false)
          })
        }
      }).catch((response) => {
        message.error(response['hydra:description'])
      }).finally(() => {
        setIsDatasetLoading(false)
      })
    }

    if (values.tasks && values.tasks.length > 0) {
      Promise.all([...values.tasks.filter((taskData: TaskData) => taskData.id).map((taskData: TaskData) => {
          return taskData.id ? updateBriefTaskData(taskData.id, {
            isClientConfirmed: taskData.isClientConfirmed,
            isEconometristAccepted: taskData.isEconometristAccepted,
          }) : null
        }
      )]).then(values => {
        message.success(t('common.saved'));
        mutateBriefTaskData();
        setIsValuesChanged(false)
      })
    }
  };

  const deleteBriefTaskFile = () => {
    if (brief && brief.data) {
      api.patch(`${urls.briefTasks}/${getBriefCommonTaskId()}`, {
        arg: {
          file: null
        }
      }).then(response => {
        message.success(t('common.saved'));
        mutateBriefTask()
      })
    }
  };

  const removeBriefTaskData = (index: number) => {
    const briefTaskDataId = datasetForm.getFieldValue(["tasks", index, "id"]);
    const briefTaskDataFile = datasetForm.getFieldValue(["tasks", index, "file"]);

    if (briefTaskDataFile) {
      deleteBriefTaskData(briefTaskDataId).then(response => {
        message.success(t('common.saved'));
        mutateBriefTaskData()
      })
    }
  };

  return (
    <Flex gap={'large'} justify={'flex-end'}>
      <Space direction={'vertical'} size={'middle'} style={{width: '640px'}}>
        <Card>
          <Flex vertical gap={'middle'}>
            <Title level={5}>{t('dataset.datasetTemplate')}</Title>
            {(briefTaskFile && briefTaskFile.data) ?
              <Flex justify={'space-between'} align={'center'}>
                <Flex
                  onClick={() => {
                    if (!Roles.hasAccess('canSetTemplateInDataset')) {
                      return;
                    }
                    api.getFile(`${process.env.REACT_APP_PUBLIC_ENTRYPOINT}${briefTaskFile.data.contentUrl}`)
                  }}
                >
                  <Flex gap={'small'}>
                    <div className={'file__item-preview'}>
                      <IconXls/>
                    </div>
                    <div className={'file__item-info'}>
                      <Flex gap={2} vertical>
                        <div className={'file__item-name'}>{t('dataset.datasetTemplate')}</div>
                        {/*<div className={'file__item-info__description'}>{briefTaskFile.data.size} </div>*/}
                      </Flex>
                    </div>
                  </Flex>
                </Flex>
                {Roles.hasAccess('canSetTemplateInDataset') &&
                  <div
                    onClick={(event) => {
                      event.preventDefault();
                      deleteBriefTaskFile();
                    }}
                    className={'delete-button'}
                  >
                    <IconDelete/>
                  </div>
                }
              </Flex>

              :
              <Form
                form={datasetTemplateForm}
                name="dataset-template"
                onFinish={onDatasetTemplateSave}
                layout="vertical"
                autoComplete="off"
                disabled={!Roles.hasAccess('canSetTemplateInDataset')}
              >
                <Form.Item
                  name="file"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <Upload name="logo" listType="picture" {...uploadDatasetTemplateProps} style={{width: '100%'}}
                          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          disabled={!Roles.hasAccess('canUploadFileInDataset')}
                  >
                    {datasetTemplateFileList.length === 0 &&
                      <Button icon={<IconUploadFile/>} size={'large'}
                              style={{width: '100%'}}>{t('dataset.uploadFile')}</Button>
                    }

                  </Upload>
                </Form.Item>
              </Form>
            }
          </Flex>
        </Card>

        {briefTask && briefTask.data &&
          <Card>
            <Flex vertical gap={'middle'}>
              <Title level={5}>{t('dataset.dataset')}</Title>
              <Form
                form={datasetForm}
                name="dataset"
                onFinish={onDatasetSave}
                layout="vertical"
                autoComplete="off"
                onValuesChange={() => {
                  setIsValuesChanged(true)
                }}
                disabled={!Roles.hasAccess('canEditDataset')}
              >
                <Flex vertical gap={'middle'}>
                  <Form.List name="tasks">
                    {(fields, {add, remove}) => (
                      <Flex vertical gap={'middle'}>
                        {fields.map(({key, name, ...restField}) => (
                          <>
                            <Row gutter={12}>
                              <Col span={20}>
                                <Flex
                                  onClick={() => {
                                    api.getFile(`${process.env.REACT_APP_PUBLIC_ENTRYPOINT}${datasetForm.getFieldValue(["tasks", name, "contentUrl"])}`)
                                  }}
                                  align={'center'}
                                >
                                  <Flex justify={'space-between'} align={'center'}>
                                    <Flex gap={'small'}>
                                      <div className={'file__item-preview'}>
                                        <IconXls/>
                                      </div>
                                      <div className={'file__item-info'}>
                                        <Flex gap={2} vertical>
                                          <div
                                            className={'file__item-name'}>{datasetForm.getFieldValue(["tasks", name, "originalName"]) || t('dataset.dataset')}</div>
                                        </Flex>
                                      </div>
                                    </Flex>
                                  </Flex>
                                </Flex>
                              </Col>
                              <Col span={4}>
                                <Flex
                                  className={'delete-button'}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    removeBriefTaskData(name)
                                  }}
                                  justify={'flex-end'}
                                >
                                  <IconDelete/>
                                </Flex>
                              </Col>
                            </Row>
                            <Row gutter={12}>
                              <Col span={24}>
                                <Flex align={'center'} gap={'small'} style={{width: '100%'}}>
                                  <Flex vertical style={{width: '100%'}}>
                                    <Flex align={'center'} gap={'small'} justify={'space-between'}
                                          style={{width: '100%'}}>
                                      <span>{t('dataset.clientConfirmed')}</span>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'isClientConfirmed']}
                                        valuePropName={'checked'}
                                      >
                                        <Checkbox
                                          disabled={!Roles.hasAccess('canSetDatasetClientConfirmed')}
                                        />
                                      </Form.Item>
                                    </Flex>
                                    <Flex align={'center'} gap={'small'} justify={'space-between'}>
                                      <span>{t('dataset.econometristAccepted')}</span>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'isEconometristAccepted']}
                                        valuePropName={'checked'}
                                      >
                                        <Checkbox
                                          disabled={!Roles.hasAccess('canSetDatasetEconometristAccepted')}
                                        />
                                      </Form.Item>
                                    </Flex>
                                  </Flex>
                                </Flex>
                              </Col>
                            </Row>
                          </>
                        ))}
                      </Flex>
                    )}
                  </Form.List>

                  {!allTasksDataAccepted() &&
                    <Form.Item
                      name="file"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <Upload name="logo" listType="picture" {...uploadDatasetProps} style={{width: '100%'}}
                              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                              disabled={!Roles.hasAccess('canUploadFileInDataset')}
                      >
                        {datasetFileList.length === 0 &&
                          <Button icon={<IconUploadFile/>} size={'large'}
                                  style={{width: '100%'}}>{t('dataset.uploadFile')}</Button>
                        }

                      </Upload>
                    </Form.Item>
                  }
                </Flex>
              </Form>
            </Flex>
          </Card>
        }

        <ButtonBlack
          onClick={() => {
            datasetTemplateForm.submit();
            datasetForm.submit()
          }}
          disabled={!isValuesChanged}
        >{t('common.save')}</ButtonBlack>
      </Space>
      <Card style={{width: '636px'}}>
        <Flex vertical gap={16}>
          <Title level={5}>{t('edit.stages')}</Title>

          <Flex vertical gap={4}>
            <Stages/>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}
